import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { ClaimAttachment, RenameClaimAttachment } from "./types";

export const useClaimAttachmentsApi = () => {
  const api = useApi();

  const useClaimAttachments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, claimId: string) => {
      const url = buildUrl(`/admin/claims/${claimId}/attachments`, query);
      return useSWR<Paginated<ClaimAttachment>, CustomError, string>(url, (url) =>
        api.get<Paginated<ClaimAttachment>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteClaimAttachment = useCallback(
    async (documentId: string, claimId: string): Promise<void> => {
      const url = `/admin/claim_attachments/${documentId}`;
      await api.del(url);
      api.mutateCache([`/admin/claims/${claimId}/attachments`]);
    },
    [api],
  );

  const insertClaimAttachment = useCallback(
    async (file: File, overWrite?: boolean, claimId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      await api.put<FormData>(`/admin/claims/${claimId}/attachments${overWrite ? "?overwrite=1" : ""}`, formData);
      api.mutateCache([`/admin/claims/${claimId}/attachments`]);
    },
    [api],
  );

  const renameClaimAttachment = useCallback(
    async (docId: string, body: RenameClaimAttachment, claimId?: string): Promise<void> => {
      await api.put<RenameClaimAttachment>(`/admin/claim_attachments/${docId}`, body);
      api.mutateCache([`/admin/claims/${claimId}/attachments`]);
    },
    [api],
  );

  const getClaimAttachment = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/claim_attachments/${docID}`;
      return api.get<FileInfo>({ url, signal });
    },
    [api],
  );

  return {
    useClaimAttachments,
    deleteClaimAttachment,
    insertClaimAttachment,
    renameClaimAttachment,
    getClaimAttachment,
  };
};
